/**
 * Entra directory sync — Step 39.
 *
 * Kai: _"We essentially need the FULL Entra schema."_
 *
 * The page an admin uses to switch it on, prove it works, and answer the only
 * two questions anyone asks afterwards: did it run, and what did it bring in.
 *
 * ── Prove it, then schedule it ─────────────────────────────────────────
 *
 * "Sync now" runs the SAME code as the nightly tick and works before the
 * schedule is enabled. That order is deliberate: reading a customer's entire
 * staff directory on a timer is not a thing to switch on and hope. A failure
 * has to be visible while somebody is watching.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Plus, RefreshCw, Users } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { apiFetch, apiSend } from '../../lib/api';
import { useConfirm } from '../../lib/confirm';
import { useDirty } from '../../lib/dirty';
import { qk } from '../../lib/queryClient';
import { fullTime, relativeTime } from '../../lib/time';

interface State {
  isEnabled: number;
  lastStatus: string;
  lastError: string | null;
  lastRunAt: number | null;
  lastFullAt: number | null;
  deltaLink: string | null;
  createdCount: number;
  updatedCount: number;
  deactivatedCount: number;
  graphConfigured: boolean;
  graphTenantId: string | null;
  syncedUsers: number;
  totalUsers: number;
}
interface Person {
  id: string;
  name: string | null;
  email: string;
  jobTitle: string | null;
  department: string | null;
  officeLocation: string | null;
  managerName: string | null;
  isActive: number;
  entraSyncedAt: number | null;
}

export function DirectorySyncPage() {
  const qc = useQueryClient();
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const state = useQuery({
    queryKey: qk.directorySync,
    queryFn: () => apiFetch<State>('/api/admin/directory'),
  });
  const recent = useQuery({
    queryKey: qk.directoryRecent,
    queryFn: () => apiFetch<{ entries: Person[] }>('/api/admin/directory/recent'),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.directorySync });
    void qc.invalidateQueries({ queryKey: qk.directoryRecent });
  };

  /**
   * One POST mutation, spelled out four times.
   *
   * There was a neat `act(path)` helper that called `useMutationLike` — and
   * `react-hooks/rules-of-hooks` was right to reject it: a hook called from a
   * function that is neither a component nor a hook works only while the call
   * order happens to be stable, and nothing enforces that it stays so. The
   * repetition is the honest version.
   */
  const post = (path: string, onOk?: (r: any) => void) => ({
    mutationFn: () => apiSend<any>(path, 'POST', {}),
    onSuccess: (r: any) => {
      if (r?.ok === false) return setError(r.error ?? 'That did not work.');
      setError(null);
      onOk?.(r);
      refresh();
    },
    onError: (e: any) => setError(String(e?.message ?? 'That did not work.')),
  });

  const syncNow = useMutation(
    post('/api/admin/directory/sync', (r) =>
      setResult(
        `${r.full ? 'Full read' : 'Incremental'} — ${r.created} added, ${r.updated} updated, ${r.deactivated} deactivated.`,
      ),
    ),
  );
  const enable = useMutation(post('/api/admin/directory/enable'));
  const disable = useMutation(post('/api/admin/directory/disable'));
  const fullResync = useMutation(
    post('/api/admin/directory/full-resync', () =>
      setResult('The next sync will re-read the whole directory.'),
    ),
  );

  if (state.isPending)
    return (
      <div className="max-w-4xl space-y-3 p-6">
        <Skeleton className="h-6 w-44" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (state.isError || !state.data)
    return (
      <Card className="m-6 max-w-4xl">
        <CardContent className="flex flex-col items-start gap-3 p-6">
          <p className="text-sm">Directory settings could not be loaded.</p>
          <Button variant="outline" size="sm" onClick={() => void state.refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );

  const s = state.data;

  return (
    <div className="max-w-4xl space-y-4 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <RefreshCw className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Directory sync</h1>
        {s.isEnabled ? (
          <Badge variant="outline" className="gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Nightly
          </Badge>
        ) : (
          <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">Off</Badge>
        )}
      </div>

      <p className="max-w-3xl text-sm text-muted-foreground">
        Brings your Microsoft Entra staff directory into the service desk, so the people pickers find somebody
        who has never contacted support and automations can route on department, location or cost centre.
        Everything Entra returns is kept, whether or not it is shown here.
      </p>

      {/* The prerequisite, stated BEFORE the button that would fail on it. */}
      {!s.graphConfigured && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="text-sm">
              <p className="font-medium">Microsoft Graph is not connected yet.</p>
              <p className="mt-1 text-muted-foreground">
                Directory sync uses the same app registration as email. Set that up under Email settings
                first, then grant it the <code className="text-xs">User.Read.All</code> application permission
                — a directory read needs its own admin consent, separate from mail.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              People from the directory
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">{s.syncedUsers}</p>
            <p className="mt-1 text-xs text-muted-foreground">of {s.totalUsers} people on this desk</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Last run</p>
            <p className="mt-1 text-2xl font-semibold">{s.lastRunAt ? relativeTime(s.lastRunAt) : 'Never'}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {s.lastRunAt ? fullTime(s.lastRunAt) : 'Run it once to check it works'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Mode</p>
            <p className="mt-1 text-2xl font-semibold">{s.deltaLink ? 'Incremental' : 'Full'}</p>
            {/* Why this matters, in one line: an incremental sync reads the
                dozen people who changed; a full one reads everyone. */}
            <p className="mt-1 text-xs text-muted-foreground">
              {s.deltaLink
                ? 'Only reads what changed since the last run'
                : 'The next run reads the whole directory'}
            </p>
          </CardContent>
        </Card>
      </div>

      {s.lastStatus === 'failed' && s.lastError && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div className="min-w-0 text-sm">
              <p className="font-medium">The last sync failed.</p>
              <p className="mt-1 break-words text-muted-foreground">{s.lastError}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}
      {result && (
        <Card className="border-emerald-500/40 bg-emerald-500/5">
          <CardContent className="flex items-center gap-2 p-4 text-sm">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> {result}
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={!s.graphConfigured || syncNow.isPending} onClick={() => syncNow.mutate()}>
          <RefreshCw /> {syncNow.isPending ? 'Syncing…' : 'Sync now'}
        </Button>
        {s.isEnabled ? (
          <Button variant="outline" onClick={() => disable.mutate()}>
            Turn off the nightly sync
          </Button>
        ) : (
          <Button variant="outline" disabled={!s.graphConfigured} onClick={() => enable.mutate()}>
            Turn on the nightly sync
          </Button>
        )}
        <Button variant="ghost" disabled={!s.deltaLink} onClick={() => fullResync.mutate()}>
          Force a full re-read
        </Button>
      </div>

      {/* Proof, not a count. "Updated 4" is believable; seeing the four people
          is what makes it checkable. */}
      <Card>
        <CardContent className="p-4">
          <p className="mb-3 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <Users className="h-3.5 w-3.5" /> Most recently synced
          </p>
          {(recent.data?.entries ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nobody yet. Run a sync and the people it finds appear here.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="pb-2 pr-4 font-medium">Name</th>
                    <th className="pb-2 pr-4 font-medium">Job title</th>
                    <th className="pb-2 pr-4 font-medium">Department</th>
                    <th className="pb-2 pr-4 font-medium">Location</th>
                    <th className="pb-2 pr-4 font-medium">Manager</th>
                    <th className="pb-2 font-medium">Synced</th>
                  </tr>
                </thead>
                <tbody>
                  {(recent.data?.entries ?? []).map((p) => (
                    <tr key={p.id} className="border-t">
                      <td className="py-2 pr-4">
                        <span className={p.isActive ? '' : 'text-muted-foreground line-through'}>
                          {p.name ?? p.email}
                        </span>
                        <span className="block text-xs text-muted-foreground">{p.email}</span>
                      </td>
                      <td className="py-2 pr-4">{p.jobTitle ?? '—'}</td>
                      <td className="py-2 pr-4">{p.department ?? '—'}</td>
                      <td className="py-2 pr-4">{p.officeLocation ?? '—'}</td>
                      <td className="py-2 pr-4">{p.managerName ?? '—'}</td>
                      <td className="py-2 text-xs text-muted-foreground">
                        {p.entraSyncedAt ? relativeTime(p.entraSyncedAt) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 153 — the other half: this one grants ACCESS, so it lives in its
          own card with its own switch, its own run and its own wording. */}
      <AgentProvisioning />

      <p className="text-xs text-muted-foreground">
        Nobody is ever deleted by a sync: a disabled or removed Entra account is marked inactive here, because
        their tickets are ours to keep. The sync above never changes anyone's access; agent provisioning below
        does, and only for the people it provisioned itself.
      </p>
    </div>
  );
}

// ── Step 153 — agent provisioning from Entra security groups ────────────────

interface Mapping {
  id: string;
  entraGroupId: string;
  entraGroupName: string;
  roleId: string;
  roleName: string;
}
interface MappingPayload {
  entries: Mapping[];
  roles: { id: string; key: string; name: string; agents: number }[];
}

/**
 * Mappings from an Entra security group to a role here. The panel says what
 * sync may and may not do in the place where it matters — beside the button
 * that does it.
 */
function AgentProvisioning() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [groupQuery, setGroupQuery] = useState('');
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const [roleId, setRoleId] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  // A mapping that is chosen but not yet added is unsaved work — small, but
  // the router guard's rule does not have a size threshold, and losing it
  // silently is the thing the rule exists to stop.
  useDirty(!!picked && !!roleId);

  const state = useQuery({
    queryKey: qk.directorySync,
    queryFn: () => apiFetch<State>('/api/admin/directory'),
  });
  const mappings = useQuery({
    queryKey: qk.directoryMappings,
    queryFn: () => apiFetch<MappingPayload>('/api/admin/directory/role-mappings'),
  });
  const groups = useQuery({
    queryKey: qk.directoryEntraGroups(groupQuery),
    queryFn: () =>
      apiFetch<{ entries: { id: string; name: string; description: string | null }[] }>(
        `/api/admin/directory/entra-groups?q=${encodeURIComponent(groupQuery)}`,
      ),
    enabled: groupQuery.trim().length >= 2,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.directoryMappings });
    void qc.invalidateQueries({ queryKey: qk.directorySync });
  };

  const add = useMutation({
    mutationFn: () =>
      apiSend('/api/admin/directory/role-mappings', 'POST', {
        entraGroupId: picked!.id,
        entraGroupName: picked!.name,
        roleId,
      }),
    onSuccess: (r) => {
      if (!r.ok) return setFailed(r.error ?? 'That could not be saved.');
      setFailed(null);
      setPicked(null);
      setGroupQuery('');
      setRoleId('');
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiSend(`/api/admin/directory/role-mappings/${id}`, 'DELETE'),
    onSuccess: (r) => {
      if (!r.ok) return setFailed(r.error ?? 'That did not work.');
      refresh();
    },
  });
  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      apiSend<any>(`/api/admin/directory/sync-agents${dryRun ? '?dryRun=1' : ''}`, 'POST', {}),
    onSuccess: (r) => {
      if (!r.ok) return setFailed(r.error ?? 'The run failed.');
      setFailed(null);
      const b = r.body;
      setNote(
        `${b.dryRun ? 'Dry run — nothing was changed. ' : ''}${b.promoted} agent${b.promoted === 1 ? '' : 's'} added or restored, ` +
          `${b.rolesGranted} role${b.rolesGranted === 1 ? '' : 's'} granted, ${b.rolesRemoved} removed, ${b.demoted} deactivated.` +
          (b.skipped?.length ? ` Skipped: ${b.skipped.join('; ')}` : ''),
      );
      refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => apiSend('/api/admin/directory/agents-enabled', 'POST', { enabled }),
    onSuccess: (r) => {
      if (!r.ok) return setFailed(r.error ?? 'That did not work.');
      refresh();
    },
  });

  const enabled = !!(state.data as any)?.agentsEnabled;
  const lastRun = (state.data as any)?.agentsLastRunAt as number | null | undefined;
  const lastError = (state.data as any)?.agentsLastError as string | null | undefined;

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <Users className="h-4 w-4 text-muted-foreground" />
              Agent provisioning
            </h2>
            <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
              Map an Entra security group to a role and its members become agents here. Membership is read
              from the GROUP, one request per mapping — not one per person — so it works the same on a
              directory of forty thousand.
            </p>
          </div>
          <Badge
            variant={enabled ? 'default' : 'secondary'}
            className={enabled ? 'bg-emerald-600 hover:bg-emerald-600' : undefined}
          >
            {enabled ? 'On, runs nightly' : 'Off'}
          </Badge>
        </div>

        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-200">
          <p className="font-medium">This grants and removes access.</p>
          <p className="mt-0.5">
            Someone who leaves a mapped group loses the roles this gave them, and is deactivated when nothing
            is left — otherwise offboarding silently fails. It never touches an agent an admin created by
            hand, and never removes a role an admin granted.
          </p>
        </div>

        {mappings.isPending && <Skeleton className="h-24 w-full" />}
        {mappings.data && (
          <>
            <div className="divide-y rounded-md border">
              {mappings.data.entries.map((m) => (
                <div key={m.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{m.entraGroupName}</span>
                    <span className="mx-1.5 text-muted-foreground">→</span>
                    {m.roleName}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (
                        await confirm({
                          title: `Remove “${m.entraGroupName} → ${m.roleName}”?`,
                          body: 'Nobody loses access at this moment — the next run takes the role from the people this mapping gave it to.',
                          confirmLabel: 'Remove mapping',
                        })
                      )
                        remove.mutate(m.id);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              {mappings.data.entries.length === 0 && (
                <p className="px-3 py-6 text-center text-xs text-muted-foreground">No mappings yet.</p>
              )}
            </div>

            <div className="rounded-md border p-3">
              <p className="mb-2 text-xs font-medium text-muted-foreground">Add a mapping</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <Input
                    value={picked ? picked.name : groupQuery}
                    onChange={(e) => {
                      setPicked(null);
                      setGroupQuery(e.target.value);
                    }}
                    placeholder="Search security groups…"
                    className="h-9 text-sm"
                    aria-label="Security group"
                  />
                  {!picked && groupQuery.trim().length >= 2 && (
                    <div className="mt-1 max-h-40 overflow-y-auto rounded-md border">
                      {groups.isPending && <p className="p-2 text-xs text-muted-foreground">Searching…</p>}
                      {groups.isError && (
                        <p className="p-2 text-xs text-destructive">The directory could not be searched.</p>
                      )}
                      {groups.data?.entries.map((g) => (
                        <button
                          key={g.id}
                          onClick={() => setPicked({ id: g.id, name: g.name })}
                          className="block w-full px-2 py-1.5 text-left text-xs hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <span className="font-medium">{g.name}</span>
                          {g.description && (
                            <span className="block truncate text-muted-foreground">{g.description}</span>
                          )}
                        </button>
                      ))}
                      {groups.data && groups.data.entries.length === 0 && (
                        <p className="p-2 text-xs text-muted-foreground">No groups match.</p>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Select
                    value={roleId}
                    onChange={(e) => setRoleId(e.target.value)}
                    className="h-9 text-sm"
                    aria-label="Role"
                  >
                    <option value="">Choose a role…</option>
                    {mappings.data.roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.agents} {r.agents === 1 ? 'agent' : 'agents'} today)
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <Button
                size="sm"
                className="mt-2"
                disabled={!picked || !roleId || add.isPending}
                onClick={() => add.mutate()}
              >
                <Plus /> Add mapping
              </Button>
            </div>
          </>
        )}

        {note && <p className="rounded-md border bg-muted/30 p-2 text-xs">{note}</p>}
        {failed && <p className="text-xs text-destructive">{failed}</p>}
        {lastError && !failed && (
          <p className="flex items-start gap-1.5 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Last run failed: {lastError}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          <span className="text-xs text-muted-foreground">
            {lastRun ? `Last run ${relativeTime(lastRun)}` : 'Never run'}
          </span>
          <div className="flex-1" />
          {/* A dry run first, always offered: "who would this affect" answered
              before anyone's access changes. */}
          <Button
            variant="outline"
            size="sm"
            disabled={run.isPending || !mappings.data?.entries.length}
            onClick={() => run.mutate(true)}
          >
            Dry run
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={run.isPending || !mappings.data?.entries.length}
            onClick={() => run.mutate(false)}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Run now
          </Button>
          <Button
            size="sm"
            variant={enabled ? 'outline' : 'default'}
            disabled={toggle.isPending}
            onClick={async () => {
              if (
                enabled ||
                (await confirm({
                  title: 'Provision agents nightly?',
                  body: 'Every night, the mapped groups decide who is an agent and what roles they hold — including taking access away from people who have left a group. Run a dry run first if you have not.',
                  confirmLabel: 'Turn on',
                }))
              )
                toggle.mutate(!enabled);
            }}
          >
            {enabled ? 'Turn off' : 'Turn on'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
